import {
  gqlEthnicAppearanceEnum,
  gqlGenderAppearanceEnum,
  gqlGenderIdentityEnum,
  gqlTalentUnionEnum,
} from '@minecraft.graphql-types';
import {
  excludeInvalidFilters,
  FiltersNameEnum,
  FilterValues,
  getAgeRangeInput,
  getDateRangeInput,
  getEntitiesBasedOnFieldName,
  getEntityIds,
  getExcludeTalentUnionsInput,
  getGenderSizeRangeInput,
  getMinMaxValueByName,
  getPetInput,
  getPetSizeInput,
  getPiercingsInput,
  getRangeInput,
  getShoeWidthInput,
  getSizeInput,
  getSkillsInput,
  getTalentUnionsInput,
  getTattoosInput,
  getVehicleInput,
  getWillingnessInput,
} from '@blocs.filter';
import { ProfileSearchInput } from '@minecraft.graphql-operations';

// TalentScoutFilterType
export const getMappedFilterQuery = (filters: FilterValues): ProfileSearchInput => {
  const genderFilter = getEntitiesBasedOnFieldName(filters[FiltersNameEnum.gender]);

  return {
    ...excludeInvalidFilters({
      stageName: filters[FiltersNameEnum.stageName],
      representativeListIds: getEntityIds({
        asNumber: true,
        array: filters[FiltersNameEnum.representativeList],
      }) as number[],
      representativeIds: getEntityIds({ asNumber: true, array: filters[FiltersNameEnum.representatives] }) as number[],
      talentUnions: getTalentUnionsInput({
        autofillByRoleCriteria: filters?.[FiltersNameEnum.autofillByRoleCriteria],
        array: filters[FiltersNameEnum.unionStatus],
      }) as gqlTalentUnionEnum[],
      excludeTalentUnions: getExcludeTalentUnionsInput({
        autofillByRoleCriteria: filters?.[FiltersNameEnum.autofillByRoleCriteria],
        array: filters[FiltersNameEnum.excludeUnionStatus],
      }) as gqlTalentUnionEnum[],
      genderAppearances: getEntityIds({ array: genderFilter[FiltersNameEnum.gender] }) as gqlGenderAppearanceEnum[],
      genderIdentities: getEntityIds({
        array: genderFilter[FiltersNameEnum.additionallyIdentifiesAs],
      }) as gqlGenderIdentityEnum[],
      ethnicAppearances: getEntityIds({
        array: filters[FiltersNameEnum.ethnicAppearance],
      }) as gqlEthnicAppearanceEnum[],
      workingLocationIds: getEntityIds({ array: filters[FiltersNameEnum.workingLocation] }),
      // Advanced Search Inputs
      hairColorId: getEntityIds({ array: filters[FiltersNameEnum.hairColor] }),
      hairStyleId: getEntityIds({ array: filters[FiltersNameEnum.hairStyle] }),
      facialHairIds: getEntityIds({ array: filters[FiltersNameEnum.facialHair] }),
      eyeColorId: getEntityIds({ array: filters[FiltersNameEnum.eyeColor] }),
      uniqueAttributeIds: getEntityIds({ array: filters[FiltersNameEnum.uniqueAttributes] }),
      multipleBirthId: getEntityIds({ array: filters[FiltersNameEnum.multipleBirth] }),
      otherExperiences: filters[FiltersNameEnum.otherExperience],
      willingnessIds: getWillingnessInput({
        willingToHairColor: filters[FiltersNameEnum.willingToHairColor],
        willingToHairStyle: filters[FiltersNameEnum.willingToHairStyle],
        willingToFacialHair: filters[FiltersNameEnum.willingToFacialHair],
        otherWilllingnessIds: getEntityIds({ array: filters[FiltersNameEnum.rolePreferences] }),
      }),
      skills: getSkillsInput(filters[FiltersNameEnum.skills], filters[FiltersNameEnum.skillLevels]),
      skillOperator: filters[FiltersNameEnum.skillOperator]?.value || filters[FiltersNameEnum.skillOperator],
      propIds: getEntityIds({ array: filters[FiltersNameEnum.props] }),
      sportEquipmentIds: getEntityIds({ asNumber: true, array: filters[FiltersNameEnum.sportsEquipment] }) as number[],
      wardrobeIds: getEntityIds({ asNumber: true, array: filters[FiltersNameEnum.wardrobe] }) as number[],
      profession: filters[FiltersNameEnum.profession],
      documentIds: getEntityIds({ array: filters[FiltersNameEnum.documents] }),
      profileListIds: getEntityIds({ asNumber: true, array: filters[FiltersNameEnum.profileListIds] }) as number[],
    }),
    ...getAgeRangeInput({
      filterName: 'playableAge',
      switchTo: filters[FiltersNameEnum.playableAgeRange]?.[FiltersNameEnum.switchTo],
      max: filters[FiltersNameEnum.playableAgeRange]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.playableAgeRange]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'heightRange',
      max: filters[FiltersNameEnum.heightRange]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.heightRange]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'weightRange',
      max: filters[FiltersNameEnum.weightRange]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.weightRange]?.[FiltersNameEnum.min],
    }),
    ...getTattoosInput({
      list: filters[FiltersNameEnum.tattoosList],
      note: filters[FiltersNameEnum.tattoosNote],
    }),
    ...getPiercingsInput({
      list: filters[FiltersNameEnum.piercingsList],
      note: filters[FiltersNameEnum.piercingsNote],
    }),
    ...getDateRangeInput({
      dateMin: filters[FiltersNameEnum.pregnancyDueDateRange]?.[FiltersNameEnum.min],
      dateMax: filters[FiltersNameEnum.pregnancyDueDateRange]?.[FiltersNameEnum.max],
      filterName: 'pregnancyDueRange',
    }),
    ...getGenderSizeRangeInput({
      filterName: 'tshirtSizeId',
      menSizeRange: {
        max: filters[FiltersNameEnum.menTShirt]?.[FiltersNameEnum.max],
        min: filters[FiltersNameEnum.menTShirt]?.[FiltersNameEnum.min],
      },
      womenSizeRange: {
        max: filters[FiltersNameEnum.womenTShirt]?.[FiltersNameEnum.max],
        min: filters[FiltersNameEnum.womenTShirt]?.[FiltersNameEnum.min],
      },
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'pantsId',
      max: filters[FiltersNameEnum.womenPants]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.womenPants]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'waistId',
      max: filters[FiltersNameEnum.waist]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.waist]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'inseamId',
      max: filters[FiltersNameEnum.inseam]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.inseam]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'gloveSizeId',
      max: filters[FiltersNameEnum.glove]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.glove]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'hatSizeId',
      max: filters[FiltersNameEnum.hat]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.hat]?.[FiltersNameEnum.min],
    }),
    ...getGenderSizeRangeInput({
      filterName: 'shoeSizeId',
      menSizeRange: getMinMaxValueByName(filters, FiltersNameEnum.menShoes),
      womenSizeRange: getMinMaxValueByName(filters, FiltersNameEnum.womenShoes),
      childrenSizeRange: getMinMaxValueByName(filters, FiltersNameEnum.childrenShoes),
    }),
    ...getShoeWidthInput({
      menShoeWidth: filters[FiltersNameEnum.menShoeWidth],
      womenShoeWidth: filters[FiltersNameEnum.womenShoeWidth],
      childrenShoeWidth: filters[FiltersNameEnum.childrenShoeWidth],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'chestSizeId',
      max: filters[FiltersNameEnum.chest]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.chest]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'neckId',
      max: filters[FiltersNameEnum.neck]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.neck]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'sleeveId',
      max: filters[FiltersNameEnum.sleeve]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.sleeve]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'coatId',
      max: filters[FiltersNameEnum.coat]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.coat]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'dressId',
      max: filters[FiltersNameEnum.dress]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.dress]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'bustId',
      max: filters[FiltersNameEnum.bust]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.bust]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'underbustId',
      max: filters[FiltersNameEnum.underbust]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.underbust]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'cupSizeId',
      max: filters[FiltersNameEnum.cup]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.cup]?.[FiltersNameEnum.min],
    }),
    ...getRangeInput({
      asNumber: true,
      filterName: 'hipSizeId',
      max: filters[FiltersNameEnum.hip]?.[FiltersNameEnum.max],
      min: filters[FiltersNameEnum.hip]?.[FiltersNameEnum.min],
    }),
    ...getSizeInput({
      boysSizeRange: {
        max: filters[FiltersNameEnum.boys]?.[FiltersNameEnum.max],
        min: filters[FiltersNameEnum.boys]?.[FiltersNameEnum.min],
      },
      girlsSizeRange: {
        max: filters[FiltersNameEnum.girls]?.[FiltersNameEnum.max],
        min: filters[FiltersNameEnum.girls]?.[FiltersNameEnum.min],
      },
      toddlersSizeRange: {
        max: filters[FiltersNameEnum.toddlers]?.[FiltersNameEnum.max],
        min: filters[FiltersNameEnum.toddlers]?.[FiltersNameEnum.min],
      },
      infantsSizeRange: {
        max: filters[FiltersNameEnum.infants]?.[FiltersNameEnum.max],
        min: filters[FiltersNameEnum.infants]?.[FiltersNameEnum.min],
      },
    }),
    ...getPetInput({
      dogBreed: filters[FiltersNameEnum.dogBreed],
      otherAnimals: filters[FiltersNameEnum.otherAnimals],
    }),
    ...getPetSizeInput(filters[FiltersNameEnum.dogBreedSize]),
    ...getVehicleInput({
      type: filters[FiltersNameEnum.vehicleType],
      yearRange: {
        max: filters[FiltersNameEnum.vehicleYearRange]?.[FiltersNameEnum.max],
        min: filters[FiltersNameEnum.vehicleYearRange]?.[FiltersNameEnum.min],
      },
      make: filters[FiltersNameEnum.vehicleMake],
      model: filters[FiltersNameEnum.vehicleModel],
    }),
    ...getDateRangeInput({
      dateMin: filters[FiltersNameEnum.workPermitExpiration]?.[FiltersNameEnum.min],
      dateMax: filters[FiltersNameEnum.workPermitExpiration]?.[FiltersNameEnum.max],
      filterName: 'workPermitExpRange',
    }),
    seekingRepresentations: getEntityIds({ array: filters[FiltersNameEnum.seekingRepresentation] }),
  };
};
